import React, { useEffect, useState, useCallback } from 'react';
import {
  Card, Row, Col, Table, Badge, Spinner, Alert
} from 'react-bootstrap';
import { useOutletContext } from 'react-router-dom';
import { adminApi } from '../services/api';
import { Boite } from '../components/Boite';
import { pourcent } from '../utils/format';
import { formatDateTime } from '../utils/datetime';
import { presentationAction } from '../utils/actions';
import type { MailboxStat, ActionStat, CorrectionStats } from '../types';

/** Au-delà de cette part de mails que le modèle n'a pas su classer, la tuile passe en rouge. */
const SEUIL_A_QUALIFIER = 20;

interface LayoutContext {
  refreshKey: number;
  onLogout: () => void;
  setLastUpdate: (d: Date) => void;
}

export const Dashboard: React.FC = () => {
  const { refreshKey, onLogout, setLastUpdate } = useOutletContext<LayoutContext>();
  const [mailboxStats, setMailboxStats] = useState<MailboxStat[]>([]);
  const [actionStats, setActionStats] = useState<ActionStat[]>([]);
  const [correctionStats, setCorrectionStats] = useState<CorrectionStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setError(null);
      const [mailboxData, actionData, corrData] = await Promise.all([
        adminApi.getMailboxStats(),
        adminApi.getActionStats(),
        adminApi.getCorrectionStats().catch(() => null)
      ]);
      setMailboxStats(mailboxData.mailboxes);
      setActionStats(actionData.actions);
      if (corrData) setCorrectionStats(corrData);
      setLastUpdate(new Date());
    } catch (err: any) {
      if (err.response?.status === 401) {
        setError('Session expirée. Reconnectez-vous.');
        setTimeout(() => onLogout(), 2000);
      } else {
        setError(err.message || 'Chargement impossible du tableau de bord');
      }
    } finally {
      setLoading(false);
    }
  }, [onLogout, setLastUpdate]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Manual refresh from layout
  useEffect(() => {
    if (refreshKey > 0) fetchData();
  }, [refreshKey, fetchData]);


  if (loading) {
    return (
      <div className="d-flex align-items-center justify-content-center" style={{ minHeight: '400px' }}>
        <div className="text-center">
          <Spinner animation="border" variant="primary" style={{ width: '3rem', height: '3rem' }} />
          <p className="mt-3 text-muted fw-semibold">Chargement...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger" className="text-center">
        <Alert.Heading>
          <i className="bi bi-exclamation-triangle-fill me-2"></i>Erreur
        </Alert.Heading>
        <p>{error}</p>
      </Alert>
    );
  }

  return (
    <>
      {/* S114 : les compteurs partent de STATS_DEPUIS ; sans elle, ils mélangent
          l'historique des tenants de démo. */}
      <div className="text-muted small mb-2">
        {correctionStats?.depuis
          ? <>Statistiques depuis le {formatDateTime(correctionStats.depuis)}</>
          : <>Statistiques sur tout l'historique de la base</>}
      </div>

      {/* Tuiles sobres : la couleur ne sert qu'à signaler un chiffre qui demande une
          réaction (à qualifier au-delà de SEUIL_A_QUALIFIER). */}
      <Row className="g-3 mb-4">
        {[
          { icone: 'bi-envelope', libelle: 'Mails', valeur: String(correctionStats?.total_processed ?? 0) },
          // Le chiffre de tête est la part de mails que le modèle n'a pas su classer.
          // « Tag seul » reste en note : il compte aussi les types qui, par règle,
          // ne reçoivent qu'un tag (offre et facture fournisseur, S42/S43).
          {
            icone: 'bi-question-circle',
            libelle: 'À qualifier',
            valeur: correctionStats ? pourcent(correctionStats.to_qualify_rate) : 'n/a',
            alerte: !!correctionStats && correctionStats.to_qualify_rate > SEUIL_A_QUALIFIER,
            note: correctionStats
              ? `Tag seul ${pourcent(correctionStats.unrouted_rate)} · tags bruts ${correctionStats.raw_tag_count}`
              : '',
          },
          // S115 : l'exactitude se mesure sur les seuls mails relus dans le
          // dashboard. Un mail que personne n'a regardé n'est ni juste ni faux.
          {
            icone: 'bi-check2-circle',
            libelle: 'Relus',
            valeur: String(correctionStats?.relus ?? 0),
            note: correctionStats?.relus
              ? `dont ${pourcent(correctionStats.taux_justes)} justes`
              : 'aucun retour pour l\'instant',
          },
          {
            icone: 'bi-flag',
            libelle: 'Signalés',
            valeur: String(correctionStats?.signales ?? 0),
            note: 'erreurs signalées dans « Mails »',
          },
        ].map((t) => (
          <Col xs={12} sm={6} lg={3} key={t.libelle}>
            <Card className="border h-100 shadow-none">
              <Card.Body className="py-3">
                <div className="text-muted small d-flex align-items-center" style={{ gap: '6px' }}>
                  <i className={`bi ${t.icone}`}></i>{t.libelle}
                </div>
                <div className="fw-bold" style={{ fontSize: '1.6rem', lineHeight: 1.2, color: t.alerte ? '#b02a37' : undefined }}>
                  {t.valeur}
                </div>
                {t.note && <div className="text-muted" style={{ fontSize: '0.75rem' }}>{t.note}</div>}
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Par boîte */}
      <Card className="border-0 shadow-sm mb-4">
        <Card.Header className="bg-white border-0 pt-3">
          <h5 className="mb-0 fw-semibold fs-6">
            <i className="bi bi-inbox me-2"></i>
            Par boîte
          </h5>
        </Card.Header>
        <Card.Body className="p-0">
          <div className="table-responsive">
            <Table hover className="mb-0">
              <thead className="table-light">
                <tr>
                  <th className="border-0">Boîte</th>
                  <th className="border-0 text-center">Mails</th>
                  <th className="border-0 text-center">Avec pièce jointe</th>
                  <th className="border-0 text-center">24 dernières h</th>
                </tr>
              </thead>
              <tbody>
                {mailboxStats.map((stat) => (
                  <tr key={stat.mailbox}>
                    <td>
                      <Boite nom={stat.mailbox} />
                    </td>
                    <td className="text-center fw-semibold">{stat.total_emails}</td>
                    <td className="text-center">
                      {stat.with_attachments}
                    </td>
                    <td className="text-center">
                      {stat.recent_24h}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>

      {/* Actions */}
      <Card className="border-0 shadow-sm">
        <Card.Header className="bg-white border-0 pt-3">
          <h5 className="mb-0 fw-semibold fs-6">
            <i className="bi bi-activity me-2"></i>
            Actions
          </h5>
        </Card.Header>
        <Card.Body className="p-0">
          <div className="table-responsive">
            <Table hover className="mb-0">
              <thead className="table-light">
                <tr>
                  <th className="border-0">Action</th>
                  <th className="border-0 text-center">Prévues</th>
                  <th className="border-0 text-center">Exécutées</th>
                  <th className="border-0 text-center">Échecs</th>
                  <th className="border-0 text-center">Annulées</th>
                  <th className="border-0 text-center">Taux de réussite</th>
                </tr>
              </thead>
              <tbody>
                {actionStats.map((stat) => {
                  const p = presentationAction(stat);
                  return (
                    <tr key={stat.action_type}>
                      <td className="fw-semibold">
                        <i className={`bi ${p.icone} me-1`} style={{ color: p.couleur }}></i>
                        {p.libelle}
                      </td>
                      <td className="text-center">{stat.prevues}</td>
                      <td className="text-center">
                        {stat.successful}
                      </td>
                      <td className="text-center">
                        <Badge bg={stat.failed > 0 ? 'danger' : 'light'} text={stat.failed > 0 ? undefined : 'dark'} pill>
                          {stat.failed}
                        </Badge>
                      </td>
                      <td className="text-center">{stat.annulees - stat.prevues}</td>
                      <td className="text-center">
                        {stat.tentees === 0 ? (
                          <span className="text-muted" title="Aucune action exécutée">n/a</span>
                        ) : (
                          <div className="progress position-relative" style={{ height: '20px' }}>
                            <div
                              className={`progress-bar ${stat.success_rate >= 90 ? 'bg-success' : stat.success_rate >= 70 ? 'bg-warning' : 'bg-danger'}`}
                              role="progressbar"
                              style={{ width: `${stat.success_rate}%` }}
                              aria-valuenow={stat.success_rate}
                              aria-valuemin={0}
                              aria-valuemax={100}
                            >
                            </div>
                            <span className="position-absolute w-100 text-center small" style={{ lineHeight: '20px', color: '#212529' }}>
                              {pourcent(stat.success_rate)} ({stat.successful} sur {stat.tentees})
                            </span>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>
    </>
  );
};
